import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { api, errorMessage } from '../api/client'
import type { Vendor } from '../api/types'
import { Card, Empty, Field, Note, NumberInput, PageHead } from '../components/ui'
import { useAuth } from '../auth/AuthContext'
import { useApi } from '../hooks/useApi'

interface CommissionPreview {
  staleRowCount: number
  everyStaleRowIsZero: boolean
  recommendBackfill: boolean
}

/** Past this many names, hunting down the list is slower than typing. */
const SEARCH_APPEARS_AFTER = 8

/**
 * The growers: a list you can read down, and one row at a time opened to edit.
 *
 * <p>Every grower used to be drawn as a full edit form, one under the other. Two
 * growers already looked like the page had repeated itself, and finding the one
 * you wanted meant reading four boxes of every card on the way down. A list
 * answers the question the page is actually asked - who do we deal with, and on
 * what commission - and the form appears only for the name being changed.
 */
export function Vendors() {
  const vendors = useApi<Vendor[]>('/vendors')
  const { isOwner } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<string | null>(null)
  const [query, setQuery] = useState('')

  const [name, setName] = useState('')
  const [pct, setPct] = useState('')
  const [qism, setQism] = useState('')
  const [phone, setPhone] = useState('')

  const all = vendors.data ?? []
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return all
    return all.filter(
      (v) =>
        v.name.toLowerCase().includes(needle) || (v.qism ?? '').toLowerCase().includes(needle),
    )
  }, [all, query])

  async function add(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setDone(null)
    try {
      await api.post('/vendors', {
        name,
        commissionPct: pct || '0',
        qism: qism || null,
        phone: phone || null,
      })
      setDone(`${name.trim()} لکھ دیا گیا۔`)
      setName('')
      setPct('')
      setQism('')
      setPhone('')
      vendors.reload()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  async function remove(vendor: Vendor) {
    setError(null)
    setDone(null)
    try {
      await api.delete(`/vendors/${vendor.id}`)
      setDone(`${vendor.name} ہٹا دیا گیا۔`)
      setConfirming(null)
      vendors.reload()
    } catch (err) {
      setError(errorMessage(err))
      setConfirming(null)
    }
  }

  return (
    <>
      <PageHead title="زمیندار" hint="نام، کمیشن اور قسم۔" />

      {error && <Note kind="error">{error}</Note>}
      {done && <Note kind="info">{done}</Note>}

      <Card title="نیا زمیندار">
        <form onSubmit={add} className="row">
          <Field label="نام">
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </Field>
          <Field label="کمیشن %">
            <NumberInput value={pct} onChange={setPct} decimals placeholder="0" />
          </Field>
          <Field label="قسم (ضروری نہیں)">
            <input value={qism} onChange={(e) => setQism(e.target.value)} maxLength={60} />
          </Field>
          <Field label="واٹس ایپ نمبر" hint="بل سیدھا بھیجنے کے لیے">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={30}
              inputMode="tel"
            />
          </Field>
          <div className="field">
            <label>&nbsp;</label>
            <button className="btn" disabled={!name.trim()}>
              لکھیں
            </button>
          </div>
        </form>
      </Card>

      <Card
        title={`لسٹ (${all.length})`}
        actions={
          all.length > SEARCH_APPEARS_AFTER ? (
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="نام ڈھونڈیں"
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #cfc9c0' }}
            />
          ) : undefined
        }
      >
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>نام</th>
                <th>کمیشن %</th>
                <th className="col-extra">قسم</th>
                <th className="col-extra">واٹس ایپ</th>
                <th className="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((vendor) => (
                <tr key={vendor.id}>
                  <td className="name-cell">{vendor.name}</td>
                  <td className="num">{vendor.commissionPct}</td>
                  <td className="col-extra">{vendor.qism || '—'}</td>
                  <td className="col-extra num">{vendor.phone || '—'}</td>
                  <td>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--small"
                        onClick={() => setEditing(editing === vendor.id ? null : vendor.id)}
                      >
                        {editing === vendor.id ? 'بند کریں' : 'بدلیں'}
                      </button>

                      {isOwner &&
                        (confirming === vendor.id ? (
                          <>
                            <button
                              type="button"
                              className="btn btn--danger btn--small"
                              onClick={() => void remove(vendor)}
                            >
                              پکا ڈیلیٹ
                            </button>
                            <button
                              type="button"
                              className="btn btn--ghost btn--small"
                              onClick={() => setConfirming(null)}
                            >
                              رہنے دیں
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="btn btn--danger btn--small"
                            onClick={() => setConfirming(vendor.id)}
                          >
                            ڈیلیٹ
                          </button>
                        ))}
                    </div>
                  </td>
                </tr>
              ))}

              {rows.length === 0 && (
                <tr>
                  <td colSpan={5}>
                    <Empty>
                      {all.length === 0 ? 'ابھی کوئی زمیندار نہیں لکھا۔' : 'اِس نام کا کوئی زمیندار نہیں۔'}
                    </Empty>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="muted small" style={{ marginTop: 10 }}>
          نام صرف اُسی وقت ہٹ سکتا ہے جب اُس کی کوئی سپلائی یا ایڈوانس کتاب میں نہ ہو۔
        </p>
      </Card>

      {editing && (
        <VendorEditor
          key={editing}
          vendor={all.find((v) => v.id === editing)!}
          onSaved={(message) => {
            setDone(message)
            setError(null)
            setEditing(null)
            vendors.reload()
          }}
          onError={setError}
        />
      )}
    </>
  )
}

/**
 * Editing a grower's terms.
 *
 * The commission on this record is only ever used for NEW deliveries. Everything
 * already in the book keeps the percentage it was taken in under, so a
 * renegotiation cannot silently restate a bill that has been settled and paid.
 *
 * The backfill checkbox is how you override that on purpose, and it is ticked
 * for you only when every stale delivery sits at zero percent - zero is what
 * gets entered when the rate was not agreed yet, so filling those in is filling
 * in a blank rather than rewriting history.
 */
function VendorEditor({
  vendor,
  onSaved,
  onError,
}: {
  vendor: Vendor
  onSaved: (message: string) => void
  onError: (message: string) => void
}) {
  const { isOwner } = useAuth()
  const [name, setName] = useState(vendor.name)
  const [pct, setPct] = useState(vendor.commissionPct)
  const [qism, setQism] = useState(vendor.qism ?? '')
  const [phone, setPhone] = useState(vendor.phone ?? '')
  const [preview, setPreview] = useState<CommissionPreview | null>(null)
  const [backfill, setBackfill] = useState(false)
  const [busy, setBusy] = useState(false)

  const pctChanged = Number(pct) !== Number(vendor.commissionPct)

  useEffect(() => {
    if (!pctChanged || !isOwner) {
      setPreview(null)
      setBackfill(false)
      return
    }

    let cancelled = false
    api
      .get<CommissionPreview>(`/vendors/${vendor.id}/commission-change-preview?pct=${pct || 0}`)
      .then((response) => {
        if (cancelled) return
        setPreview(response.data)
        setBackfill(response.data.recommendBackfill)
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [pct, pctChanged, vendor.id, isOwner])

  async function save(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    try {
      const response = await api.put<{ rowsBackfilled: number; staleRowsRemaining: number }>(
        `/vendors/${vendor.id}`,
        {
          name,
          commissionPct: pct,
          qism: qism || null,
          phone: phone || null,
          backfillOldRows: backfill,
        },
      )
      onSaved(
        response.data.rowsBackfilled > 0
          ? `${name}: ${response.data.rowsBackfilled} پرانی سپلائی پر نیا کمیشن لگا دیا۔`
          : `${name} محفوظ ہو گیا۔ پرانی سپلائی ویسی ہی رہی۔`,
      )
      setPreview(null)
    } catch (err) {
      onError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={`${vendor.name} — بدلیں`}>
      <form onSubmit={save}>
        <div className="row">
          <Field label="نام">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!isOwner}
              maxLength={120}
            />
          </Field>

          <Field label="کمیشن %">
            <NumberInput value={pct} onChange={setPct} decimals />
          </Field>

          <Field label="قسم">
            <input
              value={qism}
              onChange={(e) => setQism(e.target.value)}
              disabled={!isOwner}
              maxLength={60}
            />
          </Field>

          <Field label="واٹس ایپ نمبر">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={!isOwner}
              maxLength={30}
              inputMode="tel"
            />
          </Field>

          {isOwner && (
            <div className="field">
              <label>&nbsp;</label>
              <button className="btn" disabled={busy}>
                محفوظ کریں
              </button>
            </div>
          )}
        </div>

        {preview && preview.staleRowCount > 0 && (
          <>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={backfill}
                onChange={(e) => setBackfill(e.target.checked)}
              />
              <span>
                پرانی <strong>{preview.staleRowCount}</strong> سپلائی پر بھی یہی نیا کمیشن لگا دیں
                {preview.everyStaleRowIsZero
                  ? ' — اِن سب پر ابھی 0% ہے، یعنی ریٹ طے ہی نہیں ہوا تھا۔'
                  : ' — خبردار: اِن پر پہلے سے ایک اصلی کمیشن لگا ہوا ہے۔ بدلنے سے وہ پرانے بل بھی بدل جائیں گے جو دیے جا چکے ہیں۔'}
              </span>
            </label>

            {!preview.everyStaleRowIsZero && (
              <Note kind="warn">
                خانہ خالی رہنے دیں تو پرانی سپلائی اُسی کمیشن پر رہے گی جس پر لکھی گئی تھی، اور نیا
                کمیشن صرف آگے کی سپلائی پر لگے گا۔ عام طور پر یہی ٹھیک ہے۔
              </Note>
            )}
          </>
        )}
      </form>
    </Card>
  )
}
